import React, { useState, Fragment } from "react";
import {
  Typography,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  Button,
  Box,
  Paper,
  List,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation, useQuery } from "react-query";
import Constants, {
  FILE_TYPES,
  IMAGE_UPLOAD_ERRORS,
} from "../../services/Constants";
import VideoFileOutlinedIcon from "@mui/icons-material/VideoFileOutlined";
import AndroidOutlinedIcon from "@mui/icons-material/AndroidOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import uploadFileUsingS3Api from "../../services/AwsFileUploader.js";
import UploadListItemData from "./CreateSdvFeatureForm";
import {
  createSDVFeature,
  getServerConfiguration,
  updateSDVFeatureUploadStatus,
} from "../../services/SdvFeature";
import { useToast } from "../toast";

const FILE_CONFIGS = [
  {
    uploadType: "metadata",
    fileType: FILE_TYPES.JSON,
    uploadText: "Metadata",
    hint: ".json · click to choose",
    icon: <DescriptionOutlinedIcon fontSize="small" />,
    iconBg: "#E6F1FB",
    iconColor: "#185FA5",
  },
  {
    uploadType: "apk",
    fileType: FILE_TYPES.APK,
    uploadText: "APK file",
    hint: ".apk · click to choose",
    icon: <AndroidOutlinedIcon fontSize="small" />,
    iconBg: "#EAF3DE",
    iconColor: "#3B6D11",
  },
  {
    uploadType: "image",
    fileType: FILE_TYPES.IMAGE,
    uploadText: "Image",
    hint: ".png / .jpg · click to choose",
    icon: <ImageOutlinedIcon fontSize="small" />,
    iconBg: "#FAEEDA",
    iconColor: "#854F0B",
  },
  {
    uploadType: "video",
    fileType: FILE_TYPES.VIDEO,
    uploadText: "Preview video",
    hint: ".mp4 · click to choose",
    icon: <VideoFileOutlinedIcon fontSize="small" />,
    iconBg: "#FBEAF0",
    iconColor: "#993556",
  },
];

const SdvUploadDialog = ({
  openModal,
  setOpenModal,
  onClose,
  resumeUpload,
}) => {
  const REQUIRED_FILES_TO_UPLOAD = FILE_CONFIGS.map((f) => ({
    ...f,
    resumeUploadId: resumeUpload?.sdvFeatureId || null,
  }));

  const [fileUploadFunctions, setFileUploadFunctions] = useState({
    image: null,
    video: null,
    apk: null,
  });
  const [uploadFileMetaData, setUploadFileMetaData] = useState({
    image: { imageFileName: null, imageFileType: null, imageFileSize: null },
    video: { videoFileName: null, videoFileType: null, videoFileSize: null },
    apk: { apkFileName: null, apkFileType: null, apkFileSize: null },
  });
  const [isDisabled, setIsDisabled] = useState(true);
  const [isUploadInProgress, setIsUploadInProgress] = useState(false);
  const [description, setDescription] = useState("");
  const [featureName, setFeatureName] = useState("");
  const [metaDataFile, setMetaDataFile] = useState("");
  const [isInputDataMissing, setIsInputDataMissing] = useState(true);
  const [imageRepoBucket, setImageRepoBucket] = useState("");
  const { addToast } = useToast();
  const { UPLOAD_STATUS: UploadStatus } = Constants;

  const handleClose = () => {
    setFeatureName("");
    setDescription("");
    setIsDisabled(true);
    setIsInputDataMissing(true);
    setIsUploadInProgress(false);
    setFileUploadFunctions({ image: null, video: null, apk: null });
    setUploadFileMetaData({
      image: { imageFileName: null, imageFileType: null, imageFileSize: null },
      video: { videoFileName: null, videoFileType: null, videoFileSize: null },
      apk: { apkFileName: null, apkFileType: null, apkFileSize: null },
    });
    onClose?.();
  };

  const assignTypeFunctions = (type, func = null) => {
    fileUploadFunctions[type] = func;
    let resumeTypes = ["video", "image"];
    if (!!resumeUpload?.sdvFeatureId) {
      resumeTypes = resumeUpload?.uploadTypes;
      setIsInputDataMissing(false);
    }
    const notAvailable = resumeTypes.filter(
      (t) => t !== "apk" && fileUploadFunctions[t] === null,
    );
    setIsDisabled(!!notAvailable?.length);
  };

  useQuery("getServerConfig", getServerConfiguration, {
    refetchOnWindowFocus: false,
    onSuccess: ({ data: serverConfig = {} }) =>
      setImageRepoBucket(serverConfig.s3.IMAGE_UPLOAD_BUCKET),
    onError: ({ response: { data: errorData } }) => {
      if (errorData)
        addToast({
          type: "error",
          message: errorData.message,
          autoClose: 3000,
        });
    },
  });

  const updateImgUploadStatus = (uploadId, uploadType, uploadStatus) => {
    updateUploadStatus({ sdvFeatureId: uploadId, uploadType, uploadStatus });
  };

  const imageUploadAction = async ({ sdvFeatureId }) => {
    fileUploadFunctions["video"]?.(sdvFeatureId);
    fileUploadFunctions["image"]?.(sdvFeatureId);
    fileUploadFunctions["apk"]?.(sdvFeatureId);
  };

  const { mutate: mutateImage } = useMutation(createSDVFeature, {
    onSuccess: async ({ data }) => imageUploadAction(data),
    onError: ({ response: { data: errorData } }) => {
      if (errorData)
        addToast({
          type: "error",
          message: errorData.message,
          autoClose: 3000,
        });
    },
  });

  const { mutate: updateUploadStatus } = useMutation(
    updateSDVFeatureUploadStatus,
    {
      onSuccess: async ({ data }) => {
        if (data.isActive) {
          addToast({
            type: "success",
            message: "Uploaded successfully.",
            autoClose: 3000,
          });
          handleClose();
        }
      },
    },
  );

  const assignFileUploadMetaData = (selectedFile, type) => {
    const updates = {
      image: {
        imageFileName: selectedFile?.name,
        imageFileType: selectedFile?.type,
        imageFileSize: selectedFile?.size.toString(),
      },
      video: {
        videoFileName: selectedFile?.name,
        videoFileType: selectedFile?.type,
        videoFileSize: selectedFile?.size.toString(),
      },
      apk: {
        apkFileName: selectedFile?.name,
        apkFileType: selectedFile?.type,
        apkFileSize: selectedFile?.size.toString(),
      },
    };
    if (updates[type])
      setUploadFileMetaData((prev) => ({ ...prev, [type]: updates[type] }));
  };

  const assignFileUploadFunction = (
    type,
    selectedFile,
    stsToken,
    setUploadInstance,
    setUploadProgress,
    setErrorMsg,
    errorMsg,
    setUploadStatus,
  ) => {
    if (!!type && selectedFile !== null && !!stsToken) {
      assignFileUploadMetaData(selectedFile, type);
      const uploadFileFunc = async (id) => {
        try {
          const result = await uploadFileUsingS3Api({
            bucket: imageRepoBucket,
            key: `${type}/${id}`,
            selectedFile,
            onInit: (instance) => setUploadInstance?.(instance || null),
            onProgress: (progress) => {
              setUploadStatus(UploadStatus.InProgress);
              if (progress?.part === 2)
                updateImgUploadStatus(id, type, UploadStatus.InProgress);
              setUploadProgress({
                ...progress,
                uploadPct: progress.loaded / progress.total,
              });
            },
            creds: stsToken,
          });
          if (result) {
            setUploadStatus(UploadStatus.Completed);
            updateImgUploadStatus(id, type, UploadStatus.Completed);
          }
        } catch (error) {
          setErrorMsg(
            error.message ?? IMAGE_UPLOAD_ERRORS.SOMETHING_WENT_WRONG,
          );
          addToast({ type: "error", message: error.message, autoClose: 4000 });
          handleClose();
        }
      };
      assignTypeFunctions(type, uploadFileFunc);
    } else if (!!type && selectedFile === null) {
      assignTypeFunctions(type, null);
    }
  };

  const handleSubmission = async () => {
    const body = {
      sdvFeatureName: featureName,
      description,
      ...uploadFileMetaData.apk,
      apk_uploadStatus: UploadStatus.Started,
      ...uploadFileMetaData.image,
      image_uploadStatus: UploadStatus.Started,
      ...uploadFileMetaData.video,
      video_uploadStatus: UploadStatus.Started,
      metaDataInfo: metaDataFile || {},
      isActive: false,
    };
    setIsUploadInProgress(true);
    if (!!resumeUpload?.sdvFeatureId) {
      imageUploadAction({ sdvFeatureId: resumeUpload.sdvFeatureId });
    } else {
      mutateImage(body);
    }
  };

  const visibleFiles = REQUIRED_FILES_TO_UPLOAD.filter((item) => {
    if (!item.resumeUploadId) return true;
    return resumeUpload?.uploadTypes?.includes(item.uploadType);
  });

  return (
    <Dialog
      open={openModal}
      onClose={handleClose}
      scroll="paper"
      PaperProps={{
        sx: {
          minWidth: 520,
          maxWidth: 560,
          borderRadius: 3,
          border: "0.5px solid",
          borderColor: "divider",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          pt: 2.5,
          pb: 1,
          fontSize: "16px",
          fontWeight: 500,
        }}
      >
        {resumeUpload?.sdvFeatureId ? "Resume upload" : "Create new feature"}
        <IconButton
          onClick={handleClose}
          size="small"
          aria-label="Close"
          sx={{
            border: "0.5px solid",
            borderColor: "divider",
            borderRadius: 1,
            color: "text.secondary",
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Typography
        sx={{ px: 3, pb: 2, fontSize: "13px", color: "text.secondary" }}
      >
        Add a new feature for the companion app.
      </Typography>

      <Box sx={{ height: "0.5px", bgcolor: "divider" }} />

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Stack spacing={2.5}>
          {!resumeUpload?.sdvFeatureId && (
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}
            >
              <Stack spacing={0.75}>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "text.secondary",
                    letterSpacing: "0.04em",
                  }}
                >
                  Feature name
                </Typography>
                <TextField
                  size="small"
                  placeholder="e.g. demoFeature4"
                  value={featureName}
                  onChange={(e) => {
                    setFeatureName(e.currentTarget.value);
                    setIsInputDataMissing(
                      !e.currentTarget.value || !description,
                    );
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontSize: "13px",
                      borderRadius: 1.5,
                    },
                  }}
                />
              </Stack>
              <Stack spacing={0.75}>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "text.secondary",
                    letterSpacing: "0.04em",
                  }}
                >
                  Description
                </Typography>
                <TextField
                  size="small"
                  placeholder="Short description"
                  value={description}
                  onChange={(e) => {
                    setDescription(e.currentTarget.value);
                    setIsInputDataMissing(
                      !e.currentTarget.value || !featureName,
                    );
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontSize: "13px",
                      borderRadius: 1.5,
                    },
                  }}
                />
              </Stack>
            </Box>
          )}

          <Stack spacing={1}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 500,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "text.disabled",
              }}
            >
              Upload files
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 1.25,
              }}
            >
              {visibleFiles.map((item) => (
                <UploadListItemData
                  key={item.uploadType}
                  {...item}
                  assignFileUploadFunction={assignFileUploadFunction}
                  setMetaDataFile={setMetaDataFile}
                  isUploadInProgress={isUploadInProgress}
                />
              ))}
            </Box>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "0.5px solid",
          borderColor: "divider",
          gap: 1,
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          size="small"
          sx={{
            borderRadius: 1.5,
            textTransform: "none",
            fontSize: "13px",
            borderColor: "divider",
            color: "text.secondary",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmission}
          variant="contained"
          size="small"
          disabled={isDisabled || isUploadInProgress || isInputDataMissing}
          sx={{
            borderRadius: 1.5,
            textTransform: "none",
            fontSize: "13px",
            fontWeight: 500,
            bgcolor: "#1a3c8f",
            "&:hover": { bgcolor: "#142e6e" },
          }}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SdvUploadDialog;
